/**
 * KioskCategorySelect - Category selection screen with big buttons
 * Shows when customer first enters ordering mode
 */
import { 
  Coffee, IceCream, Apple, UtensilsCrossed, 
  Croissant, CakeSlice, Candy, GlassWater, Milk, Gift, Clock,
  ShoppingCart, ChevronRight
} from 'lucide-react';
import { useKiosk, BULACCINO_LOGO } from './KioskContext';
import KioskCoffeeClubRedemption from './KioskCoffeeClubRedemption';

// Category icons mapping
const CATEGORY_ICONS = {
  'build-your-own': UtensilsCrossed,
  'coffee': Coffee,
  'iced-drinks': GlassWater,
  'smoothies-blends': Apple,
  'pies': CakeSlice,
  'salads': Apple,
  'pastries': Croissant,
  'breads': Croissant,
  'breakfast': Croissant,
  'lunch': UtensilsCrossed,
  'milk-shakes': Milk,
  'grab-and-go': Coffee,
  'condiments': UtensilsCrossed,
  'kids': IceCream,
  'fresh-mixed-juice': GlassWater,
  'desserts': CakeSlice,
  'sweets': Candy,
  'muffins': CakeSlice
};

// Category colors mapping
const CATEGORY_COLORS = {
  'build-your-own': 'bg-indigo-600 hover:bg-indigo-700',
  'coffee': 'bg-amber-700 hover:bg-amber-800',
  'iced-drinks': 'bg-cyan-600 hover:bg-cyan-700',
  'smoothies-blends': 'bg-green-600 hover:bg-green-700',
  'pies': 'bg-red-700 hover:bg-red-800',
  'salads': 'bg-emerald-600 hover:bg-emerald-700',
  'pastries': 'bg-orange-600 hover:bg-orange-700',
  'breads': 'bg-yellow-700 hover:bg-yellow-800',
  'breakfast': 'bg-yellow-600 hover:bg-yellow-700',
  'lunch': 'bg-red-600 hover:bg-red-700',
  'milk-shakes': 'bg-pink-500 hover:bg-pink-600',
  'grab-and-go': 'bg-gray-600 hover:bg-gray-700',
  'condiments': 'bg-slate-600 hover:bg-slate-700',
  'kids': 'bg-blue-500 hover:bg-blue-600',
  'fresh-mixed-juice': 'bg-orange-500 hover:bg-orange-600',
  'desserts': 'bg-purple-600 hover:bg-purple-700',
  'sweets': 'bg-rose-500 hover:bg-rose-600',
  'muffins': 'bg-amber-600 hover:bg-amber-700'
};

export default function KioskCategorySelect() {
  const { 
    categoryTabs, setActiveCategory, setView, isAccessible, 
    cartCount, cartTotal, resetKiosk,
    customer, coffeeClubStatus, handleAddCoffeeClubRedemption
  } = useKiosk();

  const handleCategorySelect = (categoryId) => {
    setActiveCategory(categoryId);
    setView('ordering-items');  // New view for showing items
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white" data-testid="kiosk-category-select">
      {/* Cart Banner - Centered at top when items in cart */}
      {cartCount > 0 && (
        <div className="bg-primary/90 py-4 px-6">
          <button
            onClick={() => setView('cart')}
            className="w-full flex items-center justify-center gap-4 text-white touch-manipulation"
            style={{ touchAction: 'manipulation' }}
            data-testid="kiosk-view-cart-btn"
          >
            <ShoppingCart className="w-8 h-8" />
            <span className={`${isAccessible ? 'text-3xl' : 'text-2xl'} font-bold`}>
              View Cart ({cartCount} items) - ${cartTotal.toFixed(2)}
            </span>
            <ChevronRight className="w-8 h-8" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className={`bg-gray-900 ${isAccessible ? 'p-6' : 'p-4'} flex items-center justify-between`}>
        <div className="flex items-center gap-4">
          <div className="bg-white rounded-xl p-2">
            <img 
              src={BULACCINO_LOGO}
              alt="Bulaccino"
              className={isAccessible ? 'w-12 h-12' : 'w-10 h-10'}
            />
          </div>
          <div>
            <h1 className={`${isAccessible ? 'text-3xl' : 'text-2xl'} font-bold`}>
              {customer ? `Welcome, ${customer.first_name || 'Member'}!` : 'What would you like?'}
            </h1>
            <p className={`${isAccessible ? 'text-base' : 'text-sm'} text-gray-400`}>Select a category to view items</p>
          </div>
        </div>

        {/* Start Over Button */}
        <button
          onClick={resetKiosk}
          className={`${isAccessible ? 'px-5 py-3 text-base' : 'px-4 py-2 text-sm'} rounded-xl border border-red-600 text-red-400 hover:bg-red-600/20 active:bg-red-600/30 transition-colors touch-manipulation`}
          style={{ touchAction: 'manipulation' }}
          data-testid="kiosk-start-over-btn"
        >
          Start Over
        </button>
      </div>

      {/* Coffee Pass Redemption Banner - Shows status and directs to coffee menu */}
      {customer && coffeeClubStatus?.has_active_pass && (
        <div className={`${isAccessible ? 'px-8 pt-6' : 'px-6 pt-4'}`}>
          <KioskCoffeeClubRedemption />
        </div>
      )}

      {/* Category Grid */}
      <div className={`${isAccessible ? 'p-8' : 'p-6'}`}>
        <div className="grid grid-cols-3 gap-6 max-w-5xl mx-auto">
          {categoryTabs.map((category) => {
            const IconComponent = CATEGORY_ICONS[category.id] || Coffee;
            const colorClass = CATEGORY_COLORS[category.id] || 'bg-gray-700 hover:bg-gray-600';
            
            return (
              <button
                key={category.id}
                onClick={() => handleCategorySelect(category.id)}
                className={`${colorClass} ${isAccessible ? 'p-8' : 'p-6'} rounded-2xl flex flex-col items-center justify-center gap-3 transition-all transform hover:scale-[1.02] active:scale-[0.97] active:brightness-90 shadow-xl touch-manipulation`}
                style={{ touchAction: 'manipulation' }}
                data-testid={`kiosk-category-btn-${category.id}`}
              >
                <IconComponent className={`${isAccessible ? 'w-14 h-14' : 'w-12 h-12'} text-white`} />
                <span className={`${isAccessible ? 'text-xl' : 'text-lg'} font-bold text-white text-center`}>
                  {category.label}
                </span>
                <span className="text-white/70 text-sm">
                  {category.count} {category.count === 1 ? 'item' : 'items'}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
